// Performance utilities for code splitting and performance optimization
import { lazy, ComponentType } from 'react';
import { logger } from '../services/logger';

// Performance monitoring utilities
export const trackPerformance = () => {
  if (typeof window !== 'undefined' && 'performance' in window) {
    // Track Core Web Vitals
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'largest-contentful-paint') {
          logger.performance('LCP', entry.startTime, { scope: 'web-vitals' });
        }
        if (entry.entryType === 'first-input') {
          const fidEntry = entry as PerformanceEventTiming;
          const fid = fidEntry.processingStart - fidEntry.startTime;
          logger.performance('FID', fid, { scope: 'web-vitals' });
        }
        if (entry.entryType === 'layout-shift') {
          const cls = (entry as PerformanceEntry & { value: number }).value;
          logger.performance('CLS', cls * 1000, { scope: 'web-vitals' }); // Convert to ms for consistency
        }
      }
    });

    observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] });
  }
};

// Resource hints for faster loading
export const addResourceHints = () => {
  if (typeof document !== 'undefined') {
    const fontUrl = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
    
    // Preload critical fonts
    const fontPreload = document.createElement('link');
    fontPreload.rel = 'preload';
    fontPreload.href = fontUrl;
    fontPreload.as = 'style';
    document.head.appendChild(fontPreload);

    // Immediately add the stylesheet link to use the preloaded resource
    const fontStylesheet = document.createElement('link');
    fontStylesheet.rel = 'stylesheet';
    fontStylesheet.href = fontUrl;
    fontStylesheet.crossOrigin = 'anonymous';
    document.head.appendChild(fontStylesheet);

    // Preconnect to external domains
    const preconnectDomains = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://firebase.googleapis.com',
      'https://firestore.googleapis.com'
    ];

    preconnectDomains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = domain;
      document.head.appendChild(link);
    });
  }
};

// Preload critical routes after initial load
export const preloadCriticalRoutes = () => {
  // Preload most commonly used routes
  const criticalRoutes = [
    () => import('../pages/Calendar'),
    () => import('../pages/HealthInsights/HealthInsightsRedesigned'),
    () => import('../pages/Pets/PetForm')
  ];

  // Load critical routes in background
  setTimeout(() => {
    criticalRoutes.forEach(routeLoader => {
      routeLoader().catch((error) => {
        logger.error('Failed to preload critical route', error as Error, {
          scope: 'performance'
        });
      });
    });
  }, 1000);
};

// Enhanced lazy loading with retry logic
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createLazyComponent = <T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  retries: number = 3
): React.LazyExoticComponent<T> => {
  return lazy(() => {
    const loadComponent = (attempt: number = 1): Promise<{ default: T }> => {
      return importFunc().catch((error) => {
        if (attempt < retries) {
          logger.warn(`Failed to load component, retrying... (${attempt}/${retries})`, {
            attempt,
            retries,
            scope: 'lazy-loading'
          });
          return new Promise((resolve) => {
            setTimeout(() => resolve(loadComponent(attempt + 1)), 1000 * attempt);
          });
        }
        throw error;
      });
    };
    
    return loadComponent();
  });
};

// Bundle analysis utilities
export const analyzeBundle = () => {
  if (process.env.NODE_ENV === 'development') {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    logger.info('Bundle Analysis', {
      bundleSize: navEntry?.transferSize || 'Unknown',
      loadTime: navEntry?.loadEventEnd || 'Unknown',
      scope: 'performance'
    });
  }
};

// Service worker registration for caching
export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          logger.info('Service Worker registered', {
            swScope: registration.scope,
            updateViaCache: registration.updateViaCache,
            scope: 'service-worker'
          });
        })
        .catch((registrationError) => {
          logger.error('Service Worker registration failed', registrationError as Error, {
            scope: 'service-worker'
          });
        });
    });
  }
};

// Memory usage monitoring
export const monitorMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = (performance as unknown as { memory: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
    logger.info('Memory usage', {
      usedMB: Math.round(memory.usedJSHeapSize / 1048576),
      totalMB: Math.round(memory.totalJSHeapSize / 1048576),
      limitMB: Math.round(memory.jsHeapSizeLimit / 1048576),
      scope: 'performance'
    });
  }
};

// Chunk loading optimization
export const optimizeChunkLoading = () => {
  // Add chunk loading timeout
  type WebpackWindow = Window & { __webpack_require__?: (moduleId: string) => unknown };
  const webpackWindow = window as WebpackWindow;
  const originalImport = webpackWindow.__webpack_require__;
  if (originalImport) {
    webpackWindow.__webpack_require__ = function(moduleId: string) {
      const startTime = performance.now();
      const result = originalImport(moduleId);
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (duration > 1000) {
        logger.warn(`Slow chunk loading: ${moduleId} took ${duration}ms`, {
          moduleId,
          duration,
          scope: 'performance'
        });
      }
      
      return result;
    };
  }
};